import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { deriveDisplayName, deriveInitials } from '../../lib/identity';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { Bell, Search, Sparkles, Coins, Compass, Users, Calendar, Clock, Phone, FileText, Activity, User, Shield, Link2, Eye, Zap, Layers, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAdoption, PHASES } from '../shared/AdoptionJourney';
import { AdoptionLightbox } from '../shared/AdoptionJourney';
import AppSwitcher from '../shared/AppSwitcher';
import CrossAppNotifications from '../shared/CrossAppNotifications';
import ConnectionStatusIndicator from '../shared/ConnectionStatusIndicator';
import { useAuth } from '../../contexts/AuthContext';
import { useAppMode } from '../../contexts/AppModeContext';

const PHASE_COLORS = { 1: 'border-white/20 bg-white/10 text-white', 2: 'border-white/20 bg-white/10 text-white', 3: 'border-white/20 bg-white/10 text-white', 4: 'border-white/20 bg-white/10 text-white' };
const PHASE_ICONS = { 1: Eye, 2: Zap, 3: Link2, 4: Layers };

const AdoptionPhaseBadge = ({ onOpen }) => {
  const { phase } = useAdoption();
  const currentPhase = PHASES.find(p => p.id === phase);
  const Icon = PHASE_ICONS[phase] || Eye;
  return (
    <Badge variant="outline" className={cn("text-[10px] gap-1 px-2 py-1 font-medium cursor-pointer hover:opacity-80 transition-opacity", PHASE_COLORS[phase])} data-testid="adoption-phase-badge" onClick={onOpen}>
      <Icon className="h-2.5 w-2.5" /> {currentPhase?.label || 'Assist'}
    </Badge>
  );
};

const SEARCH_SUGGESTIONS = [
  { type: 'patient', icon: Users, label: 'Amanda Rodriguez', sub: 'DOB: 05/14/1988 | New Patient | BCBS PPO', href: '/echo/patients' },
  { type: 'patient', icon: Users, label: 'John Smith', sub: 'DOB: 03/15/1965 | Follow-up | Aetna HMO', href: '/echo/patients' },
  { type: 'patient', icon: Users, label: 'Maria Garcia', sub: 'DOB: 08/22/1978 | Established | UHC PPO', href: '/echo/patients' },
  { type: 'appointment', icon: Calendar, label: 'John Smith — 9:00 AM', sub: 'Today | Follow-up | Dr. Chen | Room 1', href: '/echo/schedule' },
  { type: 'appointment', icon: Calendar, label: 'Maria Garcia — 9:30 AM', sub: 'Today | New Patient | Dr. Wilson | Room 2', href: '/echo/schedule' },
  { type: 'appointment', icon: Calendar, label: 'Robert Johnson — 10:00 AM', sub: 'Today | Sick Visit | Dr. Chen | Waiting', href: '/echo/schedule' },
  { type: 'appointment', icon: Calendar, label: 'Emily Davis — 10:30 AM', sub: 'Today | Follow-up | Dr. Chen | Room 3', href: '/echo/schedule' },
  { type: 'phone', icon: Phone, label: 'Amanda Rodriguez — Callback', sub: 'Requested 2:00 PM | Re: insurance verification', href: '/echo/patients' },
  { type: 'billing', icon: FileText, label: 'INV-2026-0042 — John Smith', sub: '$30 copay collected | $120 pending insurance', href: '/echo/billing' },
  { type: 'billing', icon: FileText, label: 'INV-2026-0038 — Emily Davis', sub: '$45 copay due | Follow-up visit', href: '/echo/billing' },
];

const TYPE_LABELS = { patient: 'Patients', appointment: 'Appointments', phone: 'Callbacks', billing: 'Billing' };

const CREDIT_PLANS = [
  { id: 1, credits: 100, price: 100, label: 'Starter' },
  { id: 2, credits: 300, price: 300, label: 'Standard', popular: true },
  { id: 3, credits: 1000, price: 1000, label: 'Professional' },
];

const CREDIT_USAGE = [
  { action: 'Lens Scan', credits: 1, icon: Sparkles, used: 28, color: 'text-violet-600 bg-violet-100' },
  { action: 'AI Agent Task', credits: 1, icon: Shield, used: 15, color: 'text-emerald-600 bg-emerald-100' },
  { action: 'Voice Call (AI)', credits: 1, icon: Activity, used: 12, color: 'text-blue-600 bg-blue-100' },
];

const EchoTopbar = ({ sidebarCollapsed, onStartTour }) => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showAdoption, setShowAdoption] = useState(false);
  const [role, setRole] = useState('front_desk');
  const searchRef = useRef(null);

  // Identity fallback chain (vendor-alignment phase 1):
  //   display_name → name → first+last → email-local-part → 'User'
  // NEVER literal 'Front Desk' / 'FD' / persona — those leak across
  // sessions while the auth/me payload is still hydrating.
  const displayName = deriveDisplayName(user);
  // Neutral fallback — NEVER hardcode a specific user's email.
  const displayEmail = user?.email || '';
  const initials = deriveInitials(user);

  const balance = 385;
  const totalUsed = CREDIT_USAGE.reduce((s, u) => s + u.used * u.credits, 0);

  const query = searchQuery.toLowerCase().trim();
  const filtered = query.length > 0 ? SEARCH_SUGGESTIONS.filter(s => s.label.toLowerCase().includes(query) || s.sub.toLowerCase().includes(query)) : [];
  const grouped = filtered.reduce((acc, s) => { if (!acc[s.type]) acc[s.type] = []; acc[s.type].push(s); return acc; }, {});

  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchFocused(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 h-14 bg-black flex items-center justify-between px-5 z-50" data-testid="echo-topbar">
        {/* Left: App Switcher + Role Toggle + Search */}
        <div className="flex items-center gap-4 flex-1">
          <AppSwitcher currentApp="echo" collapsed={false} variant="topbar" />

          <div className="h-5 w-px bg-white/15" />
          <div className="relative w-full max-w-lg" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
            <Sparkles className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-white/30" />
            <Input placeholder="Search patients, appointments, billing..." className="pl-9 pr-9 bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm h-9 focus-visible:ring-white/20" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onFocus={() => setSearchFocused(true)} data-testid="ai-search-input" />
            {searchFocused && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-slate-200 shadow-xl z-50 overflow-hidden max-h-[400px] overflow-y-auto" data-testid="ai-search-dropdown">
                {query.length === 0 ? (
                  <div className="p-4">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2"><Sparkles className="h-3.5 w-3.5 text-violet-500" /><span className="font-medium">AI-powered search</span></div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(isDemo ? ['Amanda Rodriguez', "Today's appointments", 'Copay due', 'Callback list'] : ["Today's appointments", 'Copay due', 'Callback list']).map((h, i) => (
                        <button key={i} onClick={() => setSearchQuery(h)} className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors" data-testid={`search-hint-${i}`}>{h}</button>
                      ))}
                    </div>
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-4 text-center"><p className="text-sm text-slate-500">No results for "{searchQuery}"</p></div>
                ) : (
                  Object.entries(grouped).map(([type, items]) => (
                    <div key={type}>
                      <div className="px-3 py-2 bg-slate-50 border-b"><p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{TYPE_LABELS[type]}</p></div>
                      {items.slice(0, 3).map((item, i) => {
                        const Icon = item.icon;
                        return (
                          <button key={i} onClick={() => { setSearchQuery(''); setSearchFocused(false); navigate(item.href); }} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 transition-colors text-left" data-testid={`search-result-${type}-${i}`}>
                            <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-slate-600" /></div>
                            <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{item.label}</p><p className="text-xs text-slate-400 truncate">{item.sub}</p></div>
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1 px-2.5 py-1 text-[10px] font-medium cursor-pointer hover:bg-white/10 border-white/20 text-white transition-colors" onClick={onStartTour} data-testid="start-tour-btn">
            <Compass className="h-2.5 w-2.5" /> Tour
          </Badge>

          <AdoptionPhaseBadge onOpen={() => setShowAdoption(true)} />

          <Badge variant="outline" className="gap-1.5 px-2.5 py-1 text-[11px] font-semibold cursor-pointer hover:bg-white/10 border-amber-500/40 text-amber-400 transition-colors" onClick={() => setShowCredits(true)} data-testid="credits-badge">
            <Coins className="h-3 w-3" /> {balance} Credits
          </Badge>

          <ConnectionStatusIndicator />

          <CrossAppNotifications currentApp="echo" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 h-8 hover:bg-white/10" data-testid="user-profile-trigger">
                <div className="h-7 w-7 rounded-full bg-white/15 flex items-center justify-center text-[10px] font-bold text-white" data-testid="user-initials">{initials}</div>
                <div className="text-left hidden sm:block">
                  <p className="text-xs font-medium text-white" data-testid="user-display-name">{displayName}</p>
                </div>
                <ChevronDown className="h-3 w-3 text-white/50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end">
              <DropdownMenuLabel className="font-normal"><p className="text-sm font-medium" data-testid="dropdown-user-name">{displayName}</p><p className="text-xs text-muted-foreground" data-testid="dropdown-user-email">{displayEmail}</p></DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>Profile</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/echo/settings')}>Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600" onClick={() => { signOut(); navigate('/'); }} data-testid="sign-out-btn">Log out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Credits Lightbox */}
      <Dialog open={showCredits} onOpenChange={setShowCredits}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="credits-lightbox">
          <div className="bg-black p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div><p className="text-2xl font-bold">{balance}</p><p className="text-sm text-slate-400">Credits remaining</p></div>
              <Badge className="bg-white/10 text-white text-xs">{totalUsed} used this month</Badge>
            </div>
            <DialogTitle className="sr-only">Echo Credits</DialogTitle>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <p className="text-sm font-semibold mb-3">Credit Usage by AI Action</p>
              <div className="space-y-2">
                {CREDIT_USAGE.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200" data-testid={`credit-usage-${i}`}>
                      <div className="flex items-center gap-2.5">
                        <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", item.color.split(' ')[1])}><Icon className={cn("h-4 w-4", item.color.split(' ')[0])} /></div>
                        <div><p className="text-sm font-medium">{item.action}</p><p className="text-[11px] text-muted-foreground">{item.credits} credit per action</p></div>
                      </div>
                      <span className="text-sm font-semibold">{item.used * item.credits} used</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-3">Add Credits</p>
              <div className="grid grid-cols-3 gap-2">
                {CREDIT_PLANS.map(plan => (
                  <button key={plan.id} onClick={() => { toast.success(`${plan.credits} credits added`); setShowCredits(false); }}
                    className={cn("relative p-3 rounded-xl border-2 text-center hover:border-slate-400 transition-all", plan.popular ? "border-slate-900" : "border-slate-200")} data-testid={`credit-plan-${plan.id}`}>
                    {plan.popular && <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 bg-black text-white text-[9px] px-2 py-0">Popular</Badge>}
                    <p className="text-lg font-bold">{plan.credits}</p>
                    <p className="text-xs text-muted-foreground">${plan.price}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AdoptionLightbox open={showAdoption} onOpenChange={setShowAdoption} />
    </>
  );
};

export default EchoTopbar;
